import { ActionIcon, Loader, Tooltip, UnstyledButton } from "@mantine/core";
import { SmilePlus, Volume2, VolumeX } from "lucide-react";
import { useRef, useState } from "react";

import type { AiActor } from "../../../api/generated/model";
import { useThumbFirst } from "../../../ui/thumb-first";
import classes from "./actors.module.css";

export interface ActorCardProps {
  actor: AiActor;
  /** Opens live's preview of the actor's video. */
  onPreview: () => void;
  /** A signed link that no longer loads: the list is read again. */
  onLost: () => void;
  /** Live's "Create a Talking Head": the Studio's Talking Head with this actor and its voice. */
  onTalkingHead: () => void;
}

/** Live shows its loader when the video has not started 300ms after the hover. */
const LOADER_AFTER_MS = 300;

/**
 * Live's Global Actors card (`avatar-video-card`, captures 08, 10 and L04e): the portrait under
 * a muted, looping preview video, the actor's name at the top left. On hover the name fades, the
 * video plays (live's loader if it is slow), and once it plays, live's mute switch at the top
 * right; live's "Create a Talking Head" at the bottom opens the Studio's Talking Head with the
 * actor and its voice (#238). The card opens the preview; leaving it stops the video and mutes it
 * again.
 */
export function ActorCard({ actor, onPreview, onLost, onTalkingHead }: ActorCardProps) {
  // The 512 px portrait, not the 1,536 px one, at the card's 155 to 182 px (issue 828, item 12).
  const picture = useThumbFirst(actor.thumb_url, actor.image_url, onLost);
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [slow, setSlow] = useState(false);
  const [muted, setMuted] = useState(true);
  const waiting = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ratio = `${actor.width} / ${actor.height}`;
  const enter = () => {
    const element = video.current;
    if (element === null) return;
    waiting.current = setTimeout(() => setSlow(true), LOADER_AFTER_MS);
    element.preload = "auto";
    void element.play()?.catch(() => undefined);
  };
  const leave = () => {
    if (waiting.current !== null) clearTimeout(waiting.current);
    setSlow(false);
    const element = video.current;
    if (element === null) return;
    element.pause();
    element.load();
    element.muted = true;
    setMuted(true);
  };
  return (
    // Hover is a preview only, as on live's card: the cover button below is the control.
    <div
      className={classes.card}
      style={{ aspectRatio: ratio }}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      <UnstyledButton className={classes.cover} aria-label={actor.name} onClick={onPreview}>
        <img className={classes.media} src={picture.src} alt="" loading="lazy" decoding="async" onError={picture.onError} />
        {actor.video_url === null ? null : (
          // A preview video has no captions to give, and plays muted until asked.
          // oxlint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={video}
            className={classes.media}
            src={actor.video_url}
            poster={picture.src}
            muted
            loop
            playsInline
            preload="none"
            aria-hidden
            onPlaying={() => {
              if (waiting.current !== null) clearTimeout(waiting.current);
              setSlow(false);
              setPlaying(true);
            }}
            onPause={() => setPlaying(false)}
          />
        )}
      </UnstyledButton>
      <span className={classes.name} aria-hidden>
        {actor.name}
      </span>
      {slow ? <Loader size={20} className={classes.corner} aria-label="Loading video" /> : null}
      {playing ? (
        <ActionIcon
          variant="filled"
          color="dark"
          size={30}
          radius="xl"
          className={classes.corner}
          aria-label={muted ? "Unmute" : "Mute"}
          onClick={() => {
            const element = video.current;
            if (element === null) return;
            element.muted = !muted;
            setMuted(!muted);
          }}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </ActionIcon>
      ) : null}
      <span className={classes.menu}>
        <Tooltip label="Create a Talking Head" withArrow>
          <ActionIcon
            variant="transparent"
            className={classes.menuButton}
            aria-label="Create a Talking Head"
            onClick={onTalkingHead}
          >
            <SmilePlus size={16} />
          </ActionIcon>
        </Tooltip>
      </span>
    </div>
  );
}
